// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { WhiteLabelConfig } from '@allesauto/core/whiteLabel';
import screens from '@allesauto/tailwind/screens.json';
import throttle from 'lodash/throttle';
import React, {
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getWhiteLabelIntegrationSlugs } from 'web/src/features/whitelabel/util/getWhiteLabelIntegrationSlugs';

import { DeviceType, TileBackground } from './consts';

/**
 * Use this hook to listen on window resize event
 */
export function useWindowSize() {
  const [size, setSize] = useState(
    typeof window !== 'undefined'
      ? [window.innerWidth, window.innerHeight]
      : [0, 0] // default value for SSR
  );

  useLayoutEffect(() => {
    const updateSize = throttle(
      () => setSize([window.innerWidth, window.innerHeight]),
      50
    );

    window.addEventListener('resize', updateSize);

    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return size;
}

export const SCREEN_BREAKPOINT = {
  SM: screens.sm,
  MD: screens.md,
  LG: screens.lg,
  XL: screens.xl,
  XXL: screens['2xl'],
};

/**
 * Use this hook to get the current screen breakpoint (width in px)
 */
export function useScreenBreakpoint() {
  const allBreakpoints = useMemo(
    () =>
      Object.entries(SCREEN_BREAKPOINT)
        .map(([, v]) => v)
        .sort((v1, v2) => v1 - v2)
        .reverse(),
    []
  );

  const screenBreakpointDefault =
    typeof window !== 'undefined'
      ? allBreakpoints.find((w) => window.innerWidth >= w)
      : undefined;

  const [screenBreakpoint, setScreenBreakpoint] = useState(
    screenBreakpointDefault ?? SCREEN_BREAKPOINT.SM // SM is default value for SSR
  );

  useLayoutEffect(() => {
    function updateBreakpoint() {
      const currentWidthBreakpoint = allBreakpoints.find(
        (w) => window.innerWidth >= w
      );

      if (
        currentWidthBreakpoint !== undefined &&
        currentWidthBreakpoint !== screenBreakpoint
      ) {
        setScreenBreakpoint(currentWidthBreakpoint);
      }
    }

    window.addEventListener('resize', updateBreakpoint);

    updateBreakpoint();

    return () => window.removeEventListener('resize', updateBreakpoint);
  }, [allBreakpoints, screenBreakpoint]);

  return screenBreakpoint;
}

/**
 * Use this hook for scrolling up effect for header
 */
export type ScrollingDirection = 'up' | 'down' | undefined;

export function useScrollingDirection(scrollingThreshold?: number) {
  const [scrollingDirection, setScrollingDirection] =
    useState<ScrollingDirection>();
  const threshold = scrollingThreshold || 0;

  useEffect(() => {
    let prevYOffset = window.scrollY;
    let prevState: ScrollingDirection;

    const handleScroll = () => {
      const newYOffset = window.scrollY;
      if (Math.abs(newYOffset - prevYOffset) > threshold) {
        const newState: ScrollingDirection =
          prevYOffset > newYOffset
            ? 'up'
            : prevYOffset < newYOffset && prevYOffset >= 0
            ? 'down'
            : undefined;

        if (newState !== prevState) {
          // prevent unnecessary state updates
          setScrollingDirection(newState);

          prevState = newState;
        }

        prevYOffset = newYOffset;
      }
    };

    const onScroll = () => window.requestAnimationFrame(handleScroll);

    window.addEventListener('scroll', onScroll, false);

    return () => {
      window.removeEventListener('scroll', onScroll, false);
    };
  }, []);

  return scrollingDirection;
}

/**
 * Use this hook to render components only during Client-side Rendering
 * Recommended aproach https://reactjs.org/docs/react-dom.html#hydrate
 */
export const useCSR = () => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
};

export const useOnClickOutside = (
  refObject: RefObject<any>,
  handler: (event: MouseEvent | TouchEvent) => void
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!refObject.current || refObject.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refObject, handler]);
};

export function useExternalScript(url: string) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${url}"]`);

    const handleLoad = () => {
      setIsLoading(false);
    };

    const handleError = () => {
      setHasError(true);
    };

    if (!existingScript) {
      const script = document.createElement('script');

      script.src = url;
      script.async = true;
      document.body.appendChild(script);
      setIsLoading(true);

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);

      return () => {
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
        document.body.removeChild(script);
      };
    }

    return () => {
      document.body.removeChild(existingScript);
    };
  }, [url]);

  return [isLoading, hasError];
}

export function useExternalStyle(url: string) {
  useEffect(() => {
    const head = document.head;
    const existingStyles = document.querySelector(`link[href="${url}"]`);

    if (!existingStyles) {
      const link = document.createElement('link');

      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = url;

      head.appendChild(link);

      return () => {
        head.removeChild(link);
      };
    }

    return () => {
      head.removeChild(existingStyles);
    };
  }, [url]);
}

/**
 * Use this hook to get number of items
 * from a row based on breakpoint
 */
export const useNumberOfItemsRow = () => {
  const breakpoint = useScreenBreakpoint();

  if (breakpoint >= SCREEN_BREAKPOINT.XL) {
    return 4;
  }

  if (breakpoint <= SCREEN_BREAKPOINT.LG && breakpoint > SCREEN_BREAKPOINT.MD) {
    return 3;
  }

  if (breakpoint <= SCREEN_BREAKPOINT.MD && breakpoint > SCREEN_BREAKPOINT.SM) {
    return 2;
  }

  if (breakpoint <= SCREEN_BREAKPOINT.SM) {
    return 1;
  }

  return 0;
};

/**
 * Use this hook to trigger a helper function
 * (callback) if container ref is in viewport
 * @param onViewportShow - callback function
 * @param selector - unique selector
 * @param options - optional intersection options
 * @returns any observed reference
 */
export const useIntersectionViewport = (
  onViewportShow: (id: string) => void,
  selector: string,
  options?: IntersectionObserverInit
) => {
  const containerRef = useRef<any>(null);

  useEffect(() => {
    // Storing containerRef for cleanup
    const observerRef = containerRef.current;
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) {
          onViewportShow(selector);
          observer.unobserve(observerRef);
        }
      },
      options
    );

    if (observerRef) {
      observer.observe(observerRef);
    }

    return () => {
      if (observerRef) {
        observer.unobserve(observerRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  return containerRef;
};

export const useDeviceTypeName = () => {
  const [deviceTypeName, setDeviceTypeName] = useState<string>(
    DeviceType.MOBILE
  );

  const breakpoint = useScreenBreakpoint();
  useEffect(() => {
    if (breakpoint >= SCREEN_BREAKPOINT.XL) {
      setDeviceTypeName(DeviceType.DESKTOP_LARGE);
    } else if (
      breakpoint <= SCREEN_BREAKPOINT.LG &&
      breakpoint > SCREEN_BREAKPOINT.MD
    ) {
      setDeviceTypeName(DeviceType.DESKTOP);
    } else if (
      breakpoint <= SCREEN_BREAKPOINT.MD &&
      breakpoint > SCREEN_BREAKPOINT.SM
    ) {
      setDeviceTypeName(DeviceType.TABLET);
    } else if (breakpoint <= SCREEN_BREAKPOINT.SM) {
      setDeviceTypeName(DeviceType.MOBILE);
    }
  }, [breakpoint]);

  return deviceTypeName;
};

export interface WhiteLabelTheme {
  text: string;
  accent: string;
  border: string;
  background: string;
}

export const useWhiteLabelThemes = (
  whiteLabel: WhiteLabelConfig,
  tile: TileBackground
): WhiteLabelTheme => {
  const excludedMerchants = ['waldhausen-buerkel-de', 'aurego-de'];

  let border;
  let background;
  const text = '!text-white';
  const accent = String(
    whiteLabel?.merchantOrigin.includes(excludedMerchants[0])
      ? '!text-black'
      : `${
          whiteLabel?.merchantOrigin.includes(excludedMerchants[1])
            ? '!text-white'
            : 'wl-text-highlight'
        }`
  );

  switch (tile) {
    case TileBackground.PURPLE:
      border = 'bg-white';
      background = String(
        !whiteLabel?.merchantOrigin.includes(excludedMerchants[1])
          ? 'wl-bg-highlight'
          : 'bg-grey80'
      );
      break;
    case TileBackground.GREEN:
      border = String(
        !whiteLabel?.merchantOrigin.includes(excludedMerchants[1])
          ? 'wl-bg-highlight'
          : 'bg-grey80'
      );
      background = '';
      break;
    case TileBackground.WHITE:
      border = '';
      background = '';
      break;
    case TileBackground.GREY:
      border = 'bg-white';
      background = String(
        !whiteLabel?.merchantOrigin.includes(excludedMerchants[1])
          ? 'wl-bg-highlight'
          : 'bg-grey80'
      );
      break;
  }

  return { text, accent, border, background } as const;
};

export const useIsWhiteLabel = (merchant: string): boolean => {
  const [slugs, updateSlugs] = React.useState<string[]>([]);

  React.useEffect(() => {
    getWhiteLabelIntegrationSlugs()
      .then((data) => updateSlugs(data))
      .catch((e) => updateSlugs([]));
  }, []);

  return slugs.includes(merchant);
};

export const usePrevious = (value: string) => {
  const ref: any = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useHandleHoverMenuCartAndUser = (isUserLoggedIn: boolean) => {
  const [open, setOpen] = useState(false);
  const [showHoverMenu, setShowHoverMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState<boolean>(false);
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const scrollingDirection = useScrollingDirection(70);
  const deviceTypeName = useDeviceTypeName();
  const isDeviceTypeMobileOrTablet =
    deviceTypeName === DeviceType.MOBILE ||
    deviceTypeName === DeviceType.TABLET;

  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const handleHoverMenu = (show: boolean) => {
    setShowHoverMenu(show);
  };

  const handleCartHoverMenu = (show: boolean) => {
    setShowCartMenu(show);
  };

  const handleUserHoverMenu = (show: boolean) => {
    setShowUserMenu(show);
  };

  useEffect(() => {
    setUserLoggedIn(isUserLoggedIn);
  }, [isUserLoggedIn]);

  return {
    isDeviceTypeMobileOrTablet,
    scrollingDirection,
    open,
    setOpen,
    showHoverMenu,
    setShowHoverMenu,
    showUserMenu,
    setShowUserMenu,
    showCartMenu,
    setShowCartMenu,
    userLoggedIn,
    handleClose,
    handleHoverMenu,
    handleCartHoverMenu,
    handleUserHoverMenu,
  };
};
